import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';

// ─── Indicateur de force du mot de passe ─────────────────────────────────────
const PasswordStrength = ({ password }) => {
  const checks = [
    { test: /.{8,}/,      label: '8 caractères minimum' },
    { test: /[A-Z]/,      label: 'Une majuscule' },
    { test: /[a-z]/,      label: 'Une minuscule' },
    { test: /[0-9]/,      label: 'Un chiffre' },
    { test: /[!@#$%&*]/, label: 'Un caractère spécial (!@#$%&*)' },
  ];
  const passed = checks.filter(c => c.test.test(password)).length;
  const pct    = (passed / checks.length) * 100;
  const color  = passed <= 2 ? 'var(--red)' : passed <= 3 ? 'var(--orange)' : 'var(--green)';
  const label  = passed <= 2 ? 'Faible' : passed <= 3 ? 'Moyen' : passed === 4 ? 'Fort' : 'Excellent';

  if (!password) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>Force du mot de passe</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{label}</span>
      </div>
      <div style={{ background: 'var(--bg3)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s, background 0.3s' }} />
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {checks.map((c, i) => {
          const ok = c.test.test(password);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11 }}>
              <span style={{ color: ok ? 'var(--green)' : 'var(--text3)', fontSize: 12 }}>
                {ok ? '✓' : '○'}
              </span>
              <span style={{ color: ok ? 'var(--text)' : 'var(--text3)' }}>{c.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export const ChangePassword = () => {
  const { user, updatePassword, logout } = useAuth();
  const navigate = useNavigate();
  const toast    = useToast();

  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [showCurr, setShowCurr] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!current.trim()) { setError('Le mot de passe actuel est requis.'); return; }
    if (next.length < 8)  { setError('Le nouveau mot de passe doit faire au moins 8 caractères.'); return; }
    if (next !== confirm)  { setError('Les deux mots de passe ne correspondent pas.'); return; }
    if (next === current)  { setError('Le nouveau mot de passe doit être différent du mot de passe actuel.'); return; }

    setLoading(true);
    try {
      await updatePassword(current, next);
      toast.success('Mot de passe changé avec succès — Veuillez vous reconnecter.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.message || 'Erreur lors du changement de mot de passe. Vérifiez votre mot de passe actuel.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 28, fontWeight: 900, letterSpacing: -1, marginBottom: 4 }}>
            <span style={{ color: 'var(--accent)' }}>LAV</span>EX
            <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400, marginLeft: 8, letterSpacing: 2 }}>ADMIN</span>
          </div>
        </div>

        {/* Card */}
        <div className="card">
          {/* Header alerte */}
          <div style={{
            background: 'rgba(240,160,74,0.08)',
            border: '1px solid rgba(240,160,74,0.25)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 24,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>🔐</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: 'var(--orange)' }}>
                Changement de mot de passe obligatoire
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                Bonjour <strong>{user?.name}</strong>, votre compte a été créé par un administrateur avec un mot de passe temporaire.
                Vous devez le changer avant de pouvoir accéder à la plateforme.
              </div>
            </div>
          </div>

          {error && (
            <div className="inline-alert inline-alert-red" style={{ marginBottom: 20 }}>
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Mot de passe actuel */}
          <div className="form-group">
            <label className="form-label">Mot de passe temporaire actuel</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurr ? 'text' : 'password'}
                className="form-input"
                placeholder="Mot de passe reçu par email"
                value={current}
                onChange={e => setCurrent(e.target.value)}
                style={{ paddingRight: 42 }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowCurr(p => !p)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text3)',
                }}
              >
                {showCurr ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {/* Séparateur */}
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 18px', opacity: 0.5 }} />

          {/* Nouveau mot de passe */}
          <div className="form-group">
            <label className="form-label">Nouveau mot de passe</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNext ? 'text' : 'password'}
                className="form-input"
                placeholder="Choisissez un mot de passe sécurisé"
                value={next}
                onChange={e => setNext(e.target.value)}
                style={{ paddingRight: 42 }}
              />
              <button
                type="button"
                onClick={() => setShowNext(p => !p)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text3)',
                }}
              >
                {showNext ? '🙈' : '👁'}
              </button>
            </div>
            <PasswordStrength password={next} />
          </div>

          {/* Confirmation */}
          <div className="form-group">
            <label className="form-label">Confirmer le nouveau mot de passe</label>
            <input
              type="password"
              className="form-input"
              placeholder="Répétez le nouveau mot de passe"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{
                borderColor: confirm && next && (confirm !== next ? 'var(--red)' : 'var(--green)'),
              }}
            />
            {confirm && next && (
              <div className="form-hint" style={{ color: confirm === next ? 'var(--green)' : 'var(--red)' }}>
                {confirm === next ? '✓ Les mots de passe correspondent' : '✗ Ne correspondent pas'}
              </div>
            )}
          </div>

          <button
            className="btn btn-primary btn-full btn-lg"
            onClick={handleSubmit}
            disabled={loading || !current || !next || next !== confirm}
            style={{ marginTop: 8 }}
          >
            {loading
              ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Changement en cours...</>
              : '🔑 Changer mon mot de passe'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleLogout}
              style={{ fontSize: 12, color: 'var(--text3)' }}
            >
              ← Se connecter avec un autre compte
            </button>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: 'var(--text3)' }}>
          Si vous n'avez pas reçu votre mot de passe temporaire, contactez votre administrateur LAVEX.
        </div>
      </div>
    </div>
  );
};
